import { useQueryClient } from "@tanstack/react-query";
import type { AutoCandidate } from "@opus-dt/shared-types";
import { Notify, toast } from "@/components/ui/notify";
import { Pill } from "@/components/ui/pill";
import { useAutoBatch } from "@/features/simulations/hooks";
import { AutoRunProgress } from "@/features/simulations/auto/AutoRunProgress";
import { objectiveMeta } from "@/features/simulations/objective/objective-meta";
import { applyCandidateToDraft } from "@/features/simulations/sandbox/candidate-apply";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";
import { objectiveFromLastRun } from "@/types/sim-objective-restore";
import { sameObjectiveChoice } from "@/types/sim-objective.types";
import { SimVsRealSection } from "@/features/simulations/sandbox/results/SimVsRealSection";
import type { RunKpis } from "@/types/sim-sandbox.types";
import { AnswerBlock } from "./AnswerBlock";
import { VariationCards } from "./VariationCards";

interface Props {
  batchId: string | null;
  /** A pergunta ESCOLHIDA agora. A respondida sai do próprio lote — ver `answeredObjective`. */
  objective: SimulationObjectiveChoice | null;
  /** Backtest de OP concluída: a OP do período, quando é modo executado. */
  backtestOrderId: string | null;
  /** Rascunho alterado depois da simulação ⇒ a resposta exibida não é a do arranjo atual. */
  dirty: boolean;
  onDone: () => void;
}

const ACTIVE = new Set(["QUEUED", "RUNNING"]);

/** Candidato do lote no formato que o backtest consome. */
function kpisOf(c: AutoCandidate): RunKpis {
  return {
    oee: c.oee,
    availability: c.availability,
    performance: c.performance,
    quality: c.quality,
    estimatedProduction: c.estimatedProduction,
    confidence: c.confidenceLevel,
    bottleneckNames: c.bottleneckNames,
  };
}

// A faixa de resposta: a superfície onde a pergunta escolhida é respondida.
// Substitui a coluna de cinco blocos, em que o número que respondia disputava peso visual com
// premissas e confiança. Uma execução entrega duas camadas — a configuração atual primeiro, as
// variações depois — e ambas moram aqui, ranqueadas pelo mesmo objetivo.
export function ObjectiveHeadline({ batchId, objective, backtestOrderId, dirty, onDone }: Props) {
  const qc = useQueryClient();
  const query = useAutoBatch(batchId ?? "", batchId ? 4_000 : undefined);
  const batch = query.data;

  if (!batchId) return null;

  if (query.isError) {
    return (
      <section className="rounded-md border bg-card p-4">
        <Notify.QueryError error={query.error} onRetry={() => void query.refetch()} />
      </section>
    );
  }

  if (!batch || ACTIVE.has(batch.status)) {
    return (
      <section className="rounded-md border bg-card p-4">
        <AutoRunProgress
          batchId={batchId}
          candidateTotal={batch?.candidateCount ?? 6}
          onTerminal={() => {
            void qc.invalidateQueries({ queryKey: ["auto-batches", batchId] });
            onDone();
          }}
        />
      </section>
    );
  }

  if (batch.status === "FAILED") {
    return (
      <section className="rounded-md border border-destructive/40 bg-card p-4">
        <p className="text-sm font-medium">Não foi possível responder.</p>
        <p className="mt-1 text-[13px] text-muted-foreground">
          {batch.errorMessage ?? "A simulação não pôde ser concluída."}
        </p>
      </section>
    );
  }

  const baseline = batch.candidates.find((c) => c.isBaseline) ?? null;
  const ranked = batch.candidates
    .filter((c) => !c.isBaseline && c.status === "COMPLETED")
    .sort((a, b) => (a.rankPosition ?? 99) - (b.rankPosition ?? 99));
  // "Sem ganho" é veredito do backend, dirigido pelo objetivo — não uma heurística de tela. Nesse
  // caso a resposta É a configuração atual: nenhuma variação a superou.
  const noGain = batch.status === "NO_GAIN";
  const winner = noGain ? baseline : (ranked.find((c) => c.isWinner) ?? ranked[0] ?? baseline);

  function apply(c: AutoCandidate) {
    const n = applyCandidateToDraft(c);
    if (n > 0) toast.success(`Ajuste aplicado ao rascunho (${n} máquina${n > 1 ? "s" : ""}).`);
    else toast.error("Nenhuma máquina do rascunho casou com o ajuste.");
  }

  // A pergunta RESPONDIDA está gravada no lote, e é de lá que ela vem. Vinha
  // de um estado de tela preenchido no clique de Simular: depois de um F5 esse estado nascia vazio,
  // e a resposta restaurada aparecia rotulada com a pergunta selecionada AGORA — dizendo que um
  // número de OEE responde "quanto eu produzo em 24 h". Errar o rótulo é pior que não ter selo.
  const answered = objectiveFromLastRun(batch);
  const staleObjective = !sameObjectiveChoice(answered, objective);
  const meta = answered ? objectiveMeta(answered.kind) : undefined;

  return (
    <section className="space-y-3 rounded-md border bg-card p-4">
      {dirty || staleObjective ? (
        <Pill tone="warning" withDot={false}>
          {staleObjective
            ? "Resposta da pergunta anterior — simule de novo"
            : "Rascunho alterado depois desta resposta"}
        </Pill>
      ) : null}

      <AnswerBlock
        batch={batch}
        winner={winner}
        baseline={baseline}
        question={meta?.question ?? "Resultado da simulação"}
      />

      {noGain ? (
        <p className="rounded-md border bg-muted/50 p-2.5 text-[12px] leading-relaxed text-muted-foreground">
          <b>Nenhum ajuste superou a configuração atual</b> no objetivo escolhido. Os candidatos
          avaliados seguem abaixo, para registro.
        </p>
      ) : null}

      <VariationCards batch={batch} baseline={baseline} ranked={ranked} onApply={apply} />

      {/* O backtest de OP concluída continua disponível: perdeu a coluna de resultado,
          não a função. A rodada comparada é a da CONFIGURAÇÃO ATUAL (baseline do lote). */}
      {backtestOrderId && baseline ? (
        <div className="border-t pt-3">
          <SimVsRealSection productionOrderId={backtestOrderId} sim={kpisOf(baseline)} />
        </div>
      ) : null}
    </section>
  );
}
